import React from 'react';
import SheetCanvas from './SheetCanvas';
import { PRIORITY_METRICS } from '../utils/packing';

const PRIORITY_LABEL_BY_VALUE = PRIORITY_METRICS.reduce((acc, metric) => {
  acc[metric.value] = metric.label;
  return acc;
}, {});

function makeRectKey(r) {
  return `${r.x}|${r.y}|${r.w}|${r.h}`;
}

function makePlacedKey(p) {
  return `${p.x}|${p.y}|${p.w}|${p.h}|${p.label}|${p.rotated ? 1 : 0}|${p.color}`;
}

function makeSheetLayoutKey(sheetData) {
  const placedKey = sheetData.placed.map(makePlacedKey).sort().join(';');
  const freeRectsKey = sheetData.freeRects.map(makeRectKey).sort().join(';');
  return `${sheetData.sheet.w}x${sheetData.sheet.h}::${placedKey}::${freeRectsKey}`;
}

function groupDuplicateSheets(usedSheets) {
  const grouped = [];
  const byKey = new Map();

  usedSheets.forEach((sheetData, i) => {
    const key = makeSheetLayoutKey(sheetData);
    const existing = byKey.get(key);

    if (existing) {
      existing.count += 1;
      existing.sheetNumbers.push(i + 1);
      return;
    }

    const group = {
      key,
      count: 1,
      sheetData,
      sheetNumbers: [i + 1],
    };
    byKey.set(key, group);
    grouped.push(group);
  });

  return grouped;
}

export default function ResultsPanel({ result, wasteW, wasteH }) {
  if (!result) return null;

  const {
    usedSheets,
    remaining,
    totalRequested,
    totalPlaced,
    totalSheetArea,
    totalWaste,
    totalUsable,
    efficiency,
    fulfilled,
    priorityOrder = [],
    orderModeLabel,
  } = result;

  const orderedPriorityText = priorityOrder
    .map((metric, idx) => `${idx + 1}. ${PRIORITY_LABEL_BY_VALUE[metric] || metric}`)
    .join('  ');
  const groupedSheets = groupDuplicateSheets(usedSheets);

  const sheetUsage = (() => {
    const counts = new Map();
    for (const { sheet } of usedSheets) {
      const key = `${sheet.w}×${sheet.h}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return [...counts.entries()].map(([size, n]) => `${n}× ${size} cm`);
  })();
  const toM2 = (areaCm2) => areaCm2 / 10000;

  return (
    <div style={{ marginTop: 32 }}>
      {/* Summary stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Sheets used</div>
          <div className="stat-value">{usedSheets.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Material efficiency</div>
          <div className={`stat-value ${efficiency >= 70 ? 'good' : 'warn'}`}>{efficiency}%</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Glass used</div>
          <div className="stat-value">{toM2(totalPlaced).toFixed(2)} m²</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Waste</div>
          <div className={`stat-value ${totalWaste === 0 ? 'good' : 'warn'}`}>{toM2(totalWaste).toFixed(2)} m²</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Reusable offcuts</div>
          <div className="stat-value good">{toM2(totalUsable).toFixed(2)} m²</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total sheet area</div>
          <div className="stat-value">{toM2(totalSheetArea).toFixed(2)} m²</div>
        </div>
      </div>

      {sheetUsage.length > 0 && (
        <div className="sheet-usage-bar">
          <span className="sheet-usage-label">Sheets used:</span>
          {sheetUsage.map((s) => (
            <span key={s} className="sheet-usage-chip">{s}</span>
          ))}
        </div>
      )}

      {orderModeLabel && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
          Order option: {orderModeLabel}
        </div>
      )}

      {orderedPriorityText && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Ranked by: {orderedPriorityText}
        </div>
      )}

      {/* Status banner */}
      <div className={`status-banner ${fulfilled ? 'status-ok' : 'status-err'}`}>
        {fulfilled ? (
          <>
            <span className="status-icon">✓</span>
            Full order fulfilled using {usedSheets.length} sheet{usedSheets.length !== 1 ? 's' : ''}.
            {totalUsable > 0 && ` ${totalUsable.toFixed(2)} cm² of usable offcuts returned to stock.`}
          </>
        ) : (
          <>
            <span className="status-icon">!</span>
            Order cannot be fully fulfilled — {remaining.length} piece{remaining.length !== 1 ? 's' : ''} uncut.
            ({totalPlaced.toFixed(1)} / {totalRequested.toFixed(1)} cm² placed)
            Add more stock sheets and re-optimize.
            <div style={{ marginTop: 6, fontSize: 12 }}>
              Missing: {remaining.map((p, i) => <span key={i} style={{ marginRight: 8 }}>{p.label}</span>)}
            </div>
          </>
        )}
      </div>

      {/* Legend */}
      <div className="legend">
        <div className="legend-item">
          <div className="swatch" style={{ background: '#eaf6f0', border: '1px solid #5DCAA5' }} />
          <span>Usable offcut</span>
        </div>
        <div className="legend-item">
          <div className="swatch" style={{ background: '#fde8e8', border: '1px solid #f09595' }} />
          <span>Waste (≤{wasteW}×{wasteH} cm)</span>
        </div>
        <div className="legend-item">
          <div className="swatch" style={{ background: '#378ADD88', border: '1.5px solid #378ADD' }} />
          <span>Cut piece (↺ = rotated)</span>
        </div>
      </div>

      {/* Sheet visualizations */}
      {groupedSheets.map((group, i) => (
        <SheetCanvas
          key={group.key}
          index={i}
          sheetData={group.sheetData}
          wasteW={wasteW}
          wasteH={wasteH}
          duplicateCount={group.count}
          sheetNumbers={group.sheetNumbers}
        />
      ))}
    </div>
  );
}
